import { Collapse, IconButton, Box, Button, Checkbox, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useShowController, useTranslate } from 'react-admin'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard'
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { StatusTableCell } from '../../helper/StatusTableCell'
import { LoadingButton } from '@mui/lab'
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import InsertPageBreakOutlinedIcon from '@mui/icons-material/InsertPageBreakOutlined';
import { InvoiceItemsModal } from './modals/InvoiceItemsModal'
import { billingServices } from 'src/services/reservation/billing/billing.service'
import { CreditNoteModal } from './modals/CreditNoteModal'
import DownloadIcon from '@mui/icons-material/Download';
import { exportTable } from './logic/ExportTable'
import { HasPermission } from 'src/helper/HasPermission'
import { Visibility } from '@mui/icons-material'
import { BillingNumberModal } from './modals/BillingNumberModal'
import { CurrencyAmount } from 'src/components/uiElements/fields/CurrencyAmountChip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

export const BillableItemsTable = ({ onDocumentEmitted }) => {
    const {record} = useShowController()
    const translate = useTranslate()
    const [selectAll, setSelectAll] = useState(false)
    const [itemsSelected, setItemsSelected] = useState([])
    const [openInvoice, setOpenInvoice] = useState(false)
    const [billableItems, setBillableItems] = useState([])
    const [paginatedItems, setPaginatedItems] = useState([])
    const [isLoadingInfo, setIsLoadingInfo] = useState(false)
    const [openCreditNote, setOpenCreditNote] = useState(false)
    const [paginationData, setPaginationData] = useState({ page: 0, rowsPerPage: 10 })
    const [openBillingNumber, setOpenBillingNumber] = useState(null)
    const [isExpanded, setIsExpanded] = useState(true)

    useEffect(()=>{
        getBillingData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[])

    useEffect(()=>{
        initializePagination()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[paginationData, billableItems])

    const initializePagination = () =>{
        const paginatedData = billableItems.slice(paginationData.page*paginationData.rowsPerPage, paginationData.page*paginationData.rowsPerPage + paginationData.rowsPerPage)
        setPaginatedItems(paginatedData)
    }

    const onPageChange = (number) => {
        const paginationDataAux = { ...paginationData }
        paginationDataAux.page = number
        setPaginationData(paginationDataAux)
    }

    const onRowsPerPageChange = (e) => {
        const paginationDataAux = { ...paginationData }
        paginationDataAux.rowsPerPage = parseInt(e.target.value, 10)
        paginationDataAux.page = 0
        setPaginationData(paginationDataAux)
    }

    const getBillingData = async (no_cache = false) =>{
        setIsLoadingInfo(true)
        await billingServices.getBillingInfo(record?.id, no_cache).then(res=>{
            setBillableItems(res)
        }).finally(()=>{
            setIsLoadingInfo(false)
        })
    }

    const handleAfterClose = () => {
        getBillingData()
        onDocumentEmitted?.()
    }

    const createItemSet = (items) =>{
        const currencyValues = [...new Set(items.flatMap(x => x.currency !== undefined ? [x.currency] : []))];
        const billingCurrValues = [...new Set(items.flatMap(x => x.billing_currency !== undefined ? [x.billing_currency] : []))];

        const itemSet = Array.from(
        { length: Math.max(currencyValues.length, billingCurrValues.length) },
        (_, i) => ({
            ...(i < currencyValues.length && { currency: currencyValues[i] }),
            ...(i < billingCurrValues.length && { billing_currency: billingCurrValues[i] }),
        })
        );

        return itemSet
    }

    const onExport = () =>{
        const titles = [
            translate('ra.field.type'),
            translate('ra.field.item_description'),
            translate('ra.field.locator'),
            translate('ra.field.currency'),
            translate('ra.field.amount_to_apply'),
            translate('ra.field.currency'),
            translate('ra.field.amount_to_apply'),
            translate('ra.field.billing_status')
        ]
        const data = paginatedItems.map(item=>[
            item.product_type,
            item.detail?.name,
            item.detail?.code,
            item.currency,
            numberFormatterWithDecimalAndThousand(item.amount),
            item.billing_currency,
            numberFormatterWithDecimalAndThousand(item.billing_amount),
            translate(`ra.status.${item.billing_status}`)

        ])
        exportTable(titles, data)
    }

    const today = new Date().toISOString().split('T')[0]

    const minDueDate = itemsSelected
        .map(id => billableItems.find(item => item.item_id === id)?.due_date_final)
        .filter(Boolean)
        .filter(date => date >= today)
        .sort()
        .at(0) ?? today

    return (
        <ShowCard
            title={translate('ra.title.items')}
            posTitle={
                <IconButton
                    size='small'
                    onClick={()=>setIsExpanded(prev=>!prev)}
                    style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                    }}
                >
                    <ExpandMoreIcon />
                </IconButton>
            }
            preActionButtonJustify='flex-end'
            preActionButton={
                <Box display='flex' alignItems='center' gap={1}>
                    <HasPermission resource={'invoice'} permission='create'>
                        <Button
                            variant='contained'
                            size='small'
                            disabled={!itemsSelected.length || itemsSelected.find(it => billableItems.find(bill => bill.item_id === it)['billing_status'] === 'BILLED')}
                            onClick={()=>{
                                setOpenInvoice(true)
                            }}
                            startIcon={<InsertDriveFileOutlinedIcon/>}
                        >
                            {`${translate('ra.action.emit')} ${translate('ra.field.invoice')}`}
                        </Button>
                    </HasPermission>
                    {/* <HasPermission resource={'invoice'} permission='create'> */}
                        <Button
                            variant='outlined'
                            size='small'
                            disabled={!itemsSelected.length}
                            onClick={()=>{
                                setOpenCreditNote(true)
                            }}
                            startIcon={<InsertPageBreakOutlinedIcon/>}
                        >
                            {`${translate('ra.action.emit')} ${translate('ra.field.credit_note')}`}
                        </Button>
                    {/* </HasPermission> */}

                    <LoadingButton loading={isLoadingInfo} onClick={()=>getBillingData(true)}>
                        <RefreshIcon />
                    </LoadingButton>
                    <DownloadIcon style={{cursor:'pointer'}} color='info' onClick={onExport}/>
                </Box>
            }
        >
            <Collapse in={isExpanded} unmountOnExit style={{width:'100%'}}>
                <div style={{display:'flex', flexDirection:'row', width:'100%'}}>
                    <TableContainer style={{width:'100%'}}>
                        <Table>
                            <TableHead>
                                <TableRow style={{backgroundColor:'#f5f5f5'}}>
                                    <HasPermission resource={'invoice'} permission='create'>
                                        <TableCell>
                                            <Checkbox
                                                checked={!!selectAll}
                                                onChange={(val)=>{
                                                    const checked = val.target.checked
                                                    if(checked){
                                                        setItemsSelected(billableItems.map((item)=>item.item_id))
                                                    } else {
                                                        setItemsSelected([])
                                                    }
                                                    setSelectAll(prev=>!prev)
                                                }}
                                            />
                                        </TableCell>
                                    </HasPermission>
                                    <TableCell>{translate('ra.field.type')}</TableCell>
                                    <TableCell>{translate('ra.field.item_description')}</TableCell>
                                    <TableCell>{translate('ra.field.locator')}</TableCell>
                                    <TableCell align='right'>{translate('ra.title.services')}</TableCell>
                                    <TableCell align='right'>{translate('ra.title.billing')}</TableCell>
                                    <TableCell align='center'>{translate('ra.field.status')}</TableCell>
                                    <TableCell> </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoadingInfo ? (
                                    <TableRow>
                                        {[...Array(8)].map((_, i) => (
                                        <TableCell key={i}>
                                            <Skeleton variant='rectangular' width={'100%'} height={20} />
                                        </TableCell>
                                        ))}
                                        <HasPermission resource={'invoice'} permission='create'>
                                            <TableCell>
                                                <Skeleton variant='rectangular' width={'100%'} height={20} />
                                            </TableCell>
                                        </HasPermission>
                                    </TableRow>
                                ) : (
                                    paginatedItems.map((item, index)=>(
                                        <TableRow key={index}>
                                            <HasPermission resource={'invoice'} permission='create'>
                                                <TableCell>
                                                    <Checkbox
                                                        checked = {itemsSelected.includes(item.item_id)}
                                                        onChange={(val)=>{
                                                            const checked = val.target.checked
                                                            let auxItems = [...itemsSelected]
                                                            if(checked){
                                                                auxItems.push(item.item_id)
                                                            } else {
                                                                auxItems = auxItems.filter(auxItem=>auxItem !== item.item_id)
                                                            }
                                                            setItemsSelected(auxItems)
                                                        }}
                                                    />
                                                </TableCell>
                                            </HasPermission>
                                            <TableCell>{item.product_type}</TableCell>
                                            <TableCell>{item.detail?.name}</TableCell>
                                            <TableCell>{item.detail?.code}</TableCell>
                                            <TableCell align='right'>
                                                <CurrencyAmount currency={item.currency} amount={item.amount} />
                                            </TableCell>
                                            <TableCell align='right'>
                                                <CurrencyAmount currency={item.billing_currency} amount={item.billing_amount} />
                                            </TableCell>
                                            <StatusTableCell align='center' data={item.billing_status} inlineStatus/>
                                            <HasPermission resource={'invoice'} permission='show'>
                                                <TableCell>
                                                    <Visibility
                                                        color={item.nro ? 'info' :'disabled'}
                                                        style={{cursor:item.nro ? 'pointer' : 'inherit'}}
                                                        onClick={()=>
                                                            item.nro && setOpenBillingNumber(item.nro)
                                                        }
                                                    />
                                                </TableCell>
                                            </HasPermission>

                                        </TableRow>
                                    ))
                                )}
                                {createItemSet(billableItems).map((cur, index) => (
                                    <TableRow key={index}>
                                        <TableCell />
                                        <TableCell style={{fontWeight:'bold'}}>
                                            {translate('ra.field.total') }
                                        </TableCell>
                                        <TableCell />
                                        <HasPermission resource={'invoice'} permission='create'>
                                            <TableCell />
                                        </HasPermission>
                                        {cur.currency &&
                                            <TableCell align='right' style={{fontWeight:'bold'}}>
                                                <CurrencyAmount currency={cur.currency} amount={
                                                    billableItems.reduce((sum, item)=>
                                                        (item.currency === cur.currency ? sum + item.amount : sum), 0
                                                    )
                                                } />
                                            </TableCell>
                                        }
                                        {cur.billing_currency &&
                                            <>
                                                {!cur.currency && <TableCell />}
                                                <TableCell align='right' style={{fontWeight:'bold'}}>
                                                    <CurrencyAmount currency={cur.billing_currency} amount={
                                                        billableItems.reduce((sum, item)=>
                                                            (item.billing_currency === cur.billing_currency ? sum + item.billing_amount : sum), 0
                                                        )
                                                    } />
                                                </TableCell>
                                            </>
                                        }
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        colSpan={12}
                                        count={billableItems?.length}
                                        rowsPerPage={paginationData.rowsPerPage}
                                        page={paginationData.page}
                                        onPageChange={(e, number) => onPageChange(number)}
                                        onRowsPerPageChange={(e) => onRowsPerPageChange(e)}
                                        labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                                        labelDisplayedRows={({ from, to, count }) =>
                                            `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                                        }
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>

                </div>

                <InvoiceItemsModal
                    openInvoice={openInvoice}
                    setOpenInvoice={setOpenInvoice}
                    itemsSelected={itemsSelected}
                    setItemsSelected={setItemsSelected}
                    minDueDate={minDueDate}
                    onAfterClose={handleAfterClose}
                />

                <CreditNoteModal
                    openInvoice={openCreditNote}
                    setOpenInvoice={setOpenCreditNote}
                    itemsSelected={itemsSelected}
                    setItemsSelected={setItemsSelected}
                    minDueDate={minDueDate}
                    onAfterClose={handleAfterClose}
                />

                <BillingNumberModal
                    openBillingNumber={openBillingNumber}
                    setOpenBillingNumber={setOpenBillingNumber}
                />
            </Collapse>
        </ShowCard>
    )
}
